"use client";

import type { AnchorHTMLAttributes } from "react";
import { useCallback, useEffect, useId, useState } from "react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@templates/design-system";
import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockLogo } from "../../../shared/components/BlockLogo/BlockLogo";
import { MegaMenuResponsiveItem } from "../MegaMenuResponsiveItem/MegaMenuResponsiveItem";
import type { IMegaMenuResponsiveProps } from "./MegaMenuResponsive.props";

export const MegaMenuResponsive = ({
  actions,
  className,
  items,
  logo,
  sticky,
}: IMegaMenuResponsiveProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const [isOpen, setIsOpen] = useState(false);
  const [openItemKey, setOpenItemKey] = useState<string | null>(null);
  const idPrefix = useId();

  const handleOpenChange = useCallback((nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    if (!nextIsOpen) {
      setOpenItemKey(null);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const toggleItem = useCallback((itemKey: string) => {
    setOpenItemKey((currentItemKey) => (currentItemKey === itemKey ? null : itemKey));
  }, []);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia("(min-width: 64rem)");
    const closeWhenDesktopIsVisible = () => {
      if (desktopMediaQuery.matches) {
        handleOpenChange(false);
      }
    };

    closeWhenDesktopIsVisible();
    desktopMediaQuery.addEventListener("change", closeWhenDesktopIsVisible);

    return () => {
      desktopMediaQuery.removeEventListener("change", closeWhenDesktopIsVisible);
    };
  }, [handleOpenChange]);

  return (
    <BlockContainer
      as="header"
      disableSpacing
      fullWidth
      innerClassName="flex min-h-20 items-center gap-5 py-3 md:gap-7"
      parentClass={cn(
        "z-40 border-b border-foreground bg-background",
        sticky ? "sticky top-0" : "relative",
        className,
      )}
    >
      {logo && Link ? (
        <Link
          aria-label="Home"
          className="relative h-8 min-w-0 max-w-[14rem] flex-1 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
          href="/"
        >
          <BlockLogo
            className="h-full w-full overflow-visible"
            imageClassName="object-contain object-left"
            loading="eager"
            media={logo}
            priority
          />
        </Link>
      ) : null}
      <Sheet onOpenChange={handleOpenChange} open={isOpen}>
        <SheetTrigger asChild>
          <button
            aria-label="Open navigation menu"
            className="ml-auto inline-flex min-h-12 shrink-0 items-center justify-center rounded-full border border-foreground bg-background px-4 font-mono text-[12px]/[1.45] text-foreground transition-colors duration-200 hover:bg-surface-200 focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring"
            type="button"
          >
            <span aria-hidden="true">Menu</span>
            <span className="sr-only">Open navigation menu</span>
          </button>
        </SheetTrigger>
        <SheetContent
          className="w-full max-w-none gap-0 overflow-hidden p-0 motion-reduce:animate-none motion-reduce:transition-none sm:w-full sm:max-w-none md:w-3/4 md:max-w-sm"
          sheetOverlayClassName="motion-reduce:animate-none motion-reduce:transition-none"
          showCloseButton={false}
          side="right"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Choose a destination from the site navigation.
          </SheetDescription>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pb-8 pt-20 sm:px-8">
            <SheetClose asChild>
              <button
                aria-label="Close navigation menu"
                className="absolute right-6 top-5 inline-flex min-h-12 items-center justify-center rounded-full border border-foreground bg-background px-4 font-mono text-[12px]/[1.45] text-foreground transition-colors duration-200 hover:bg-surface-200 focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring sm:right-8"
                type="button"
              >
                <span aria-hidden="true">Close</span>
                <span className="sr-only">Close navigation menu</span>
              </button>
            </SheetClose>
            {items?.length ? (
              <nav aria-label="Primary navigation">
                <ul className="list-none">
                  {items.map((item, index) => {
                    const itemKey = String(index);
                    const panelId = `${idPrefix}-responsive-panel-${index}`;
                    const triggerId = `${idPrefix}-responsive-trigger-${index}`;

                    return (
                      <MegaMenuResponsiveItem
                        isOpen={openItemKey === itemKey}
                        item={item}
                        itemKey={itemKey}
                        key={itemKey}
                        onNavigate={closeDrawer}
                        onToggle={toggleItem}
                        panelId={panelId}
                        triggerId={triggerId}
                      />
                    );
                  })}
                </ul>
              </nav>
            ) : null}
            {actions?.length ? (
              <div className="mt-8 border-t border-foreground pt-6" onClick={closeDrawer}>
                <BlockActions actions={actions} className="flex-col items-stretch" />
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </BlockContainer>
  );
};

MegaMenuResponsive.displayName = "MegaMenu::MegaMenuResponsive";
